import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { betaInvitationService } from '../services/betaInvitationService';
import { BetaInvitationForm } from '../types/betaInvitation';

const BetaInvitationPage: React.FC = () => {
  const { t } = useTranslation();
  const [contactType, setContactType] = useState<'email' | 'phone'>('email');
  const [formData, setFormData] = useState<BetaInvitationForm>({
    name: '',
    email: '',
    phone: '',
    referral_source: '',
    pain_points: '',
    join_community: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [invitationCode, setInvitationCode] = useState('');

  // Toast state
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // Auto-hide toast after 5 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setToastMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Email validation state
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  // Email validation function
  const validateEmail = (email: string): boolean => {
    // Check if email is empty
    if (!email || email.trim() === '') {
      setEmailError(t('betaInvitation.form.validationErrors.emailRequired'));
      return false;
    }

    // Trim whitespace
    const trimmedEmail = email.trim();

    // Comprehensive email regex pattern
    // Validates: username@domain.tld
    // - Username: alphanumeric, dots, underscores, percent, plus, hyphen
    // - Domain: alphanumeric, dots, hyphen
    // - TLD: at least 2 letters
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailRegex.test(trimmedEmail)) {
      setEmailError(t('betaInvitation.form.validationErrors.emailInvalid'));
      return false;
    }

    // Additional validation: check for consecutive dots
    if (trimmedEmail.includes('..')) {
      setEmailError(t('betaInvitation.form.validationErrors.emailInvalid'));
      return false;
    }

    // Additional validation: check if starts or ends with special characters
    const localPart = trimmedEmail.split('@')[0];
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      setEmailError(t('betaInvitation.form.validationErrors.emailInvalid'));
      return false;
    }

    // Clear error if validation passes
    setEmailError('');
    return true;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));

      // Real-time validation for email (without showing error immediately)
      if (name === 'email' && value) {
        // Clear email error while typing to avoid distraction
        setEmailError('');
        // Also clear form submission error to provide clean feedback
        if (submitStatus === 'error') {
          setSubmitStatus('idle');
          setErrorMessage('');
        }
      }
    }
  };

  // Handle email field blur event
  const handleEmailBlur = () => {
    setEmailTouched(true);
    if (formData.email) {
      validateEmail(formData.email);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset previous messages first
    setSubmitStatus('idle');
    setErrorMessage('');
    setSuccessMessage('');

    // Validate email before submission if email contact method is selected
    if (contactType === 'email') {
      const isEmailValid = validateEmail(formData.email);
      setEmailTouched(true);

      if (!isEmailValid) {
        // Scroll to email field to show error
        document.getElementById('Email')?.focus();
        return;
      }
    }

    setIsSubmitting(true);

    // Prepare form data with trimmed email
    const submissionData = {
      ...formData,
      email: formData.email.trim(),
    };

    try {
      const response = await betaInvitationService.submitApplication(submissionData);

      if (response.success) {
        // If invitation code received, show success content on current page
        if (response.invitation_code) {
          setInvitationCode(response.invitation_code);
          setSubmitStatus('success');
        } else {
          // No invitation code (pending review), show success message on page
          setSubmitStatus('success');
          setSuccessMessage(response.message);
          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            referral_source: '',
            pain_points: '',
            join_community: false,
          });
          setContactType('email');
          // Reset email validation state
          setEmailError('');
          setEmailTouched(false);
        }
      } else {
        setSubmitStatus('error');
        setErrorMessage(response.message || t('betaInvitation.form.errorMessage'));
      }
    } catch (error: any) {
      setSubmitStatus('error');

      // Get user-friendly error message based on error code
      let friendlyErrorMessage = t('betaInvitation.form.errorMessage');

      if (error.code) {
        // Try to get translated error message
        const translatedError = t(`betaInvitation.form.errors.${error.code}`);
        if (translatedError && !translatedError.startsWith('betaInvitation.form.errors.')) {
          friendlyErrorMessage = translatedError;
        }
      }

      setErrorMessage(friendlyErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="main-wrapper beta-invite-page">
      {/* Toast Notification */}
      {showToast && (
        <div className="toast-container">
          <div className="toast toast-success">
            <span className="toast-icon">✓</span>
            <span className="toast-message">{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Hero Banner Section */}
      <div className="section-hero-banar is-beta-invite">
        <div className="padding-global">
          <div className="container-large">
            <div className="padding-section-medium">
              <div className="hero-banner-component">
                <div className="hero-banner-content-wrapper">
                  <h2 className="heading-style-h2">
                    {t('betaInvitation.heading')}
                  </h2>
                  <div className="hero-banar-content">
                    <Link to="/" className="home-link">
                      {t('navbar.home', 'Home')}
                    </Link>
                    <div className="hero-banar-dot dot-color-green"></div>
                    <div className="hero-banar-text text-color-green">
                      {t('navbar.betaInvite', 'Beta Invite')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="section-price-area is-beta-invite">
        <div className="padding-global">
          <div className="container-large">
            <div className="padding-section-large">
              <div className="price-area-component">
                {/* Header Section */}
                <div className="price-area-top-content-wrapper">
                  <div className="heading-wrapper">
                    <h2 className="heading-style-h2">
                      {t('betaInvitation.subheading')}
                    </h2>
                    <div className="line-color-light-green"></div>
                  </div>
                  <p className="text-size-regular">
                    {t('betaInvitation.description') || 'Join our exclusive beta program and be among the first to experience our innovative platform.'}
                  </p>
                </div>

                <div className="padding-bottom padding-xxlarge"></div>

                {/* Form Block or Success Content */}
                <div className="invitation-form-block">
                  {invitationCode ? (
                    /* Success Content - Show invitation code and next steps */
                    <>
                      {/* Invitation Code Card - 绿色边框卡片风格 */}
                      <div className="support-card-item boder-line-color-green invitation-code-card">
                        <div className="support-area-details">
                          <div className="invitation-code-label">
                            {t('betaInvitationSuccess.codeLabel')}
                          </div>
                          <div className="invitation-code-value">
                            {invitationCode}
                          </div>
                          <div className="invitation-code-hint">
                            {t('betaInvitationSuccess.codeBound')}
                          </div>
                        </div>
                      </div>

                      {/* Next Steps Section - 轻引导风格 */}
                      <div className="invitation-heading-content">
                        <div className="heading-wrapper">
                          <h3 className="heading-style-h3">{t('betaInvitationSuccess.nextSteps.title')}</h3>
                          <div className="line-color-light-blue"></div>
                        </div>
                      </div>
                      <div className="padding-bottom padding-medium"></div>

                      <div className="next-steps-list">
                        {/* Step 1 */}
                        <div className="next-step-item">
                          <div className="next-step-badge badge-orange">1</div>
                          <div className="next-step-content">
                            <div className="next-step-title">
                              {t('betaInvitationSuccess.nextSteps.step1.title')}
                            </div>
                            <div className="next-step-desc">
                              {t('betaInvitationSuccess.nextSteps.step1.desc')}
                            </div>
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div className="next-step-item">
                          <div className="next-step-badge badge-blue">2</div>
                          <div className="next-step-content">
                            <div className="next-step-title">
                              {t('betaInvitationSuccess.nextSteps.step2.title')}
                            </div>
                            <div className="next-step-desc">
                              {t('betaInvitationSuccess.nextSteps.step2.desc')}
                            </div>
                          </div>
                        </div>

                        {/* Step 3 */}
                        <div className="next-step-item">
                          <div className="next-step-badge badge-green">3</div>
                          <div className="next-step-content">
                            <div className="next-step-title">
                              {t('betaInvitationSuccess.nextSteps.step3.title')}
                            </div>
                            <div className="next-step-desc">
                              {t('betaInvitationSuccess.nextSteps.step3.desc')}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="padding-bottom padding-large"></div>

                      {/* Action Buttons - 主次分明 */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px',
                        marginTop: '20px'
                      }}>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(invitationCode);
                            setToastMessage(t('betaInvitationSuccess.codeCopied'));
                            setShowToast(true);
                          }}
                          className="button w-button"
                          style={{
                            padding: '16px 48px',
                            fontSize: '16px'
                          }}
                        >
                          {t('betaInvitationSuccess.copyCode')}
                        </button>
                        <div style={{ fontSize: '13px', color: '#999', marginBottom: '8px' }}>
                          {t('betaInvitationSuccess.laterHint')}
                        </div>
                        <button
                          onClick={() => window.location.href = '/'}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#667eea',
                            fontSize: '14px',
                            cursor: 'pointer',
                            padding: '8px 16px',
                            textDecoration: 'underline',
                            textUnderlineOffset: '3px'
                          }}
                        >
                          {t('betaInvitationSuccess.backToHome')}
                        </button>
                      </div>

                    </>
                  ) : (
                    /* Form Content - Show application form */
                    <>
                      <div className="padding-bottom padding-medium"></div>

                      <form
                        id="beta-form"
                        name="beta-form"
                        data-name="Beta Form"
                        method="post"
                        className="contact-us-form"
                        onSubmit={handleSubmit}
                        aria-label="Beta Invitation Form"
                      >
                        <div className="w-layout-grid contact-grid">
                          {/* Name */}
                          <div className="contact-field-wrapper">
                            <label htmlFor="name" className="text-saize-bold">
                              {t('betaInvitation.form.name')} <span className="text-color-pink">*</span>
                            </label>
                            <input
                              className="input w-input"
                              maxLength={256}
                              name="name"
                              data-name="Name"
                              placeholder={t('betaInvitation.form.namePlaceholder')}
                              type="text"
                              id="name"
                              required
                              value={formData.name}
                              onChange={handleInputChange}
                            />
                          </div>

                          {/* Contact Method */}
                          <div className="contact-field-wrapper">
                            <label className="text-saize-bold">
                              {t('betaInvitation.form.contactMethod')} <span className="text-color-pink">*</span>
                            </label>
                            <div className="contact-input-with-select">
                              <select
                                className="contact-type-select"
                                value={contactType}
                                onChange={(e) => {
                                  const newType = e.target.value as 'email' | 'phone';
                                  setContactType(newType);
                                  // Always reset email validation state when switching
                                  setEmailError('');
                                  setEmailTouched(false);
                                  if (newType === 'email') {
                                    setFormData(prev => ({ ...prev, phone: '' }));
                                  } else {
                                    setFormData(prev => ({ ...prev, email: '' }));
                                  }
                                }}
                              >
                                <option value="email">{t('betaInvitation.form.emailOption')}</option>
                                <option value="phone">{t('betaInvitation.form.phoneOption')}</option>
                              </select>
                              {contactType === 'email' ? (
                                <input
                                  className={`input w-input contact-input-field ${emailError && emailTouched ? 'input-error' : ''}`}
                                  maxLength={256}
                                  name="email"
                                  data-name="Email"
                                  placeholder={t('betaInvitation.form.emailPlaceholder')}
                                  type="email"
                                  id="Email"
                                  required
                                  value={formData.email}
                                  onChange={handleInputChange}
                                  onBlur={handleEmailBlur}
                                  aria-invalid={emailError && emailTouched ? 'true' : 'false'}
                                  aria-describedby={emailError && emailTouched ? 'form-error-message' : undefined}
                                />
                              ) : (
                                <input
                                  className="input w-input contact-input-field"
                                  maxLength={256}
                                  name="phone"
                                  data-name="Phone Number"
                                  placeholder={t('betaInvitation.form.phonePlaceholder')}
                                  type="tel"
                                  id="Phone-Number"
                                  required
                                  value={formData.phone}
                                  onChange={handleInputChange}
                                />
                              )}
                            </div>
                          </div>

                          {/* Referral Source */}
                          <div className="contact-field-wrapper">
                            <label htmlFor="referral" className="text-saize-bold">
                              {t('betaInvitation.form.referralSource')}
                            </label>
                            <input
                              className="input w-input"
                              maxLength={256}
                              name="referral_source"
                              data-name="Referral"
                              placeholder={t('betaInvitation.form.referralPlaceholder')}
                              type="text"
                              id="referral"
                              value={formData.referral_source}
                              onChange={handleInputChange}
                            />
                          </div>

                          {/* Pain Points */}
                          <div className="contact-field-wrapper full-width">
                            <label htmlFor="PainPoints" className="text-saize-bold">
                              {t('betaInvitation.form.painPoints')}
                            </label>
                            <textarea
                              placeholder={t('betaInvitation.form.painPointsPlaceholder')}
                              maxLength={5000}
                              id="PainPoints"
                              name="pain_points"
                              data-name="Pain Points"
                              className="input message-field w-input"
                              value={formData.pain_points}
                              onChange={handleInputChange}
                            ></textarea>
                          </div>

                          {/* Join Community Checkbox */}
                          <div className="contact-field-wrapper full-width checkbox-wrapper">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                name="join_community"
                                checked={formData.join_community}
                                onChange={handleInputChange}
                                className="checkbox-input"
                              />
                              <div className="checkbox-content">
                                <span className="text-saize-bold checkbox-title">
                                  {t('betaInvitation.form.joinCommunity')}
                                </span>
                                <span className="text-size-small checkbox-desc">
                                  {t('betaInvitation.form.joinCommunityDesc')}
                                </span>
                              </div>
                            </label>
                          </div>
                        </div>

                        <input
                          type="submit"
                          data-wait="Please wait..."
                          className="button w-button"
                          value={isSubmitting ? t('betaInvitation.form.submitting') : t('betaInvitation.form.submitButton')}
                          disabled={isSubmitting}
                        />

                        {/* Success/Error Messages Below Submit Button */}
                        {submitStatus === 'success' && !invitationCode && (
                          <div className="success-message w-form-done" tabIndex={-1} role="region" aria-label="Beta Form success" style={{ marginTop: '20px' }}>
                            <div>{successMessage || t('betaInvitation.form.successMessage')}</div>
                          </div>
                        )}

                        {/* Display email validation error or form submission error */}
                        {(emailError && emailTouched) || submitStatus === 'error' ? (
                          <div
                            id="form-error-message"
                            className="error-message w-form-fail"
                            tabIndex={-1}
                            role="alert"
                            aria-label="Beta Form failure"
                            style={{ marginTop: '20px' }}
                          >
                            <div>{emailError && emailTouched ? emailError : errorMessage}</div>
                          </div>
                        ) : null}

                        <div className="padding-bottom padding-medium"></div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetaInvitationPage;
